<script setup>
import { ref, reactive, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import FileMenu from './FileMenu.vue';
import VisaFields from './VisaFields.vue';
import AddPanel from '../AddPanel.vue';
import { useFileActions } from '../../composables/useFileActions.js';
import { useAuth } from '../../composables/useAuth.js';
import { useConfirm } from '../../composables/useConfirm.js';
import { fmtDate, sortByDate } from '../../domain/format.js';
import { isVisaDraftValid, newVisa, displayCountryName } from '../../domain/visa.js';

const props = defineProps({
  player: { type: Object, required: true },
});

const { can, ownerStamp } = useAuth();
const { confirm } = useConfirm();
const { t, locale } = useI18n();
const { upload, remove, busy } = useFileActions();
const fileInput = ref(null);
const uploadTarget = ref(null);

const canEdit = computed(() => can('travel', 'write'));
/* Objekt aus der Staff-Ansicht (F7): nur Art, Land, Status und Termine, keine Dauer und Einreiseart, keine Dateien — die Zeile
   "n Tage · Einmal/Mehrfach" entfällt dann (sonst stünde bei fehlendem Wert fälschlich "Single entry"). */
const limited = computed(() => props.player._staffView === true);

const emptyDraft = () => ({
  region: '', country: '', term: '', status: 'applied',
  duration: '', multi: false, from: '', until: '', issued: '', appdate: '',
});
const draft = reactive(emptyDraft());
const draftValid = computed(() => isVisaDraftValid(draft));

/* Neueste zuerst, echten Index mitführen — die Reihenfolge in den Daten
   bleibt unangetastet. */
const entries = computed(() =>
  sortByDate(props.player.visas || [], 'issued').map((v) => ({
    visa: v,
    idx: props.player.visas.indexOf(v),
  }))
);

/* Der Eintrag entsteht erst beim Bestätigen — bricht man ab, bleibt kein
   leeres Visum in den Daten zurück. */
function add({ close }) {
  if (!draftValid.value) return;
  if (!props.player.visas) props.player.visas = [];
  props.player.visas.push(newVisa(draft, ownerStamp()));
  Object.assign(draft, emptyDraft());
  close();
}

function resetDraft() {
  Object.assign(draft, emptyDraft());
}

/* Beschriftung in der Sprache des Betrachters. Alte Einträge haben nur den
   Freitext `type` und werden damit unverändert angezeigt. */
function titleOf(visa) {
  if (!visa.region) return visa.type || t('documents.visaUntitled');
  const place =
    visa.region === 'other'
      ? displayCountryName(visa.country, locale.value)
      : t(`documents.visaRegion.${visa.region}`);
  const term = visa.term ? t(`documents.visaTerm.${visa.term}`) : '';
  return [place, term].filter(Boolean).join(', ') || visa.type || t('documents.visaUntitled');
}

async function removeVisa(idx) {
  const visa = props.player.visas[idx];
  if (!visa) return;
  const files = visa.files || [];

  const ok = await confirm({
    title: t('confirm.deleteVisaTitle'),
    message: t('confirm.deleteVisa', { name: titleOf(visa) }),
    detail: files.length ? t('confirm.deleteVisaFiles', { count: files.length }) : '',
  });
  if (!ok) return;

  props.player.visas.splice(idx, 1);
  files.forEach((f) => remove(f));
}

function pickFile(idx) {
  uploadTarget.value = idx;
  fileInput.value.click();
}

async function onPick(event) {
  const file = event.target.files?.[0];
  event.target.value = '';
  const idx = uploadTarget.value;
  uploadTarget.value = null;
  if (!file || idx === null) return;

  const stored = await upload(file, `visa/${props.player.id}`);
  const visa = props.player.visas[idx];
  if (!visa.files) visa.files = [];
  visa.files.push(stored);
}

function removeFileAt(visa, i) {
  const [gone] = visa.files.splice(i, 1);
  if (gone) remove(gone);
}
</script>

<template>
  <div class="doc-group">
    <h3>{{ $t('documents.visas') }}</h3>

    <div v-if="!entries.length" class="feed-empty">{{ $t('documents.noVisas') }}</div>

    <div v-for="{ visa, idx } in entries" :key="idx" class="uploaded-doc stacked">
      <!-- Bearbeiten -->
      <div v-if="visa._edit && canEdit" class="entry-edit">
        <VisaFields :visa="visa" />
        <div class="er-actions">
          <button class="btn ghost small" @click="visa._edit = false">{{ $t('common.save') }}</button>
          <button class="icon-btn danger" @click="removeVisa(idx)">{{ $t('common.delete') }}</button>
        </div>
      </div>

      <!-- Ansicht -->
      <div v-else class="visa-view">
        <div class="vv-head">
          <span class="type-badge" :class="visa.status">
            {{ $t(`documents.visaStatus.${visa.status || 'applied'}`) }}
          </span>
          <div class="ud-label">{{ titleOf(visa) }}</div>
        </div>
        <div class="ud-body">
          <div v-if="visa.status === 'granted' && !limited" class="ud-sub">
            <template v-if="visa.duration">{{ visa.duration }} · </template>
            {{ visa.multi ? $t('documents.multiEntry') : $t('documents.singleEntry') }}
          </div>
          <div v-if="visa.from || visa.until" class="ud-sub">
            {{ fmtDate(visa.from) }}<template v-if="visa.until"> – {{ fmtDate(visa.until) }}</template>
          </div>
          <div v-if="visa.files?.length" class="files">
            <FileMenu
              v-for="(f, i) in visa.files"
              :key="i"
              :file="f"
              :label="$t('files.documentN', { n: i + 1 })"
              :name="titleOf(visa)"
              area="travel"
              @remove="removeFileAt(visa, i)"
            />
          </div>
        </div>
        <div v-if="canEdit" class="entry-actions">
          <button class="file-label" :disabled="busy" @click="pickFile(idx)">
            {{ busy ? $t('common.loading') : $t('files.upload') }}
          </button>
          <button class="icon-btn" @click="visa._edit = true">{{ $t('common.edit') }}</button>
        </div>
      </div>
    </div>

    <AddPanel
      v-if="canEdit"
      :label="$t('documents.addVisa')"
      :invalid="!draftValid"
      @submit="add"
      @cancel="resetDraft"
    >
      <VisaFields :visa="draft" />
    </AddPanel>

    <input v-if="canEdit" ref="fileInput" type="file" accept="application/pdf,image/*" hidden @change="onPick" />
  </div>
</template>

<style scoped>
.uploaded-doc.stacked {
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
}
/* Kopf (Status + Titel) und Inhalt untereinander; die Knöpfe rutschen auf dem
   Handy in eine eigene Zeile, statt den Titel zu überdecken. */
.visa-view {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 8px 12px;
}
.vv-head {
  flex: 1 1 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.vv-head .ud-label {
  min-width: 0;
  overflow-wrap: anywhere;
}
.visa-view .ud-body {
  flex: 1 1 200px;
  min-width: 0;
}
.entry-edit {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.er-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}
.entry-actions {
  flex: 0 0 auto;
  display: flex;
  gap: 6px;
  align-items: center;
  flex-wrap: wrap;
  margin-left: auto;
}
.files {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 4px;
}
.type-badge.granted {
  color: var(--green);
  background: var(--green-bg);
}
.type-badge.applied {
  color: var(--amber);
  background: var(--amber-bg);
}
.type-badge.rejected {
  color: var(--red);
  background: var(--red-bg);
}
.icon-btn.danger {
  color: var(--red);
}
.file-label {
  background: none;
  font: inherit;
}
</style>
